import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader } from './ui/card';
import { EmptyState } from './ui/EmptyState';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  ChevronUp, 
  Lightbulb, 
  MapPin, 
  Clock, 
  Link2, 
  AlertCircle, 
  Users,
  Search,
  ArrowRight,
  User,
  X,
  Dna,
  Calendar,
  Filter,
  Bookmark,
  BookmarkCheck,
  FileText,
  CheckCircle2,
  Circle,
  Loader2
} from 'lucide-react';

import { treeHref, personHref } from '../utils/prettyArchiveLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Confidence badge styling - subtle, investigative tone
const ConfidenceBadge = ({ confidence }) => {
  const styles = {
    verified: 'bg-primary/10 text-primary border-primary/30',
    likely: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
    possible: 'bg-secondary text-muted-foreground border-border',
    unverified: 'bg-secondary/50 text-muted-foreground/70 border-border'
  };
  
  const labels = {
    verified: 'Verified',
    likely: 'Likely',
    possible: 'Possible',
    unverified: 'Unverified'
  };
  
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-[0.1em] font-medium ${styles[confidence] || styles.unverified}`}>
      {labels[confidence] || 'Unknown'}
    </span>
  );
};

// Icon for insight type
const InsightIcon = ({ type }) => {
  const iconClass = "w-3.5 h-3.5 text-[#C9A961]";
  
  switch (type) {
    case 'pattern':
      return <Users className={iconClass} />;
    case 'migration':
      return <MapPin className={iconClass} />;
    case 'timeline':
      return <Clock className={iconClass} />;
    case 'gap':
      return <AlertCircle className={iconClass} />;
    case 'connection':
      return <Link2 className={iconClass} />;
    case 'dna_insight':
    case 'dna_match':
      return <Dna className={iconClass} />;
    case 'archive_record':
      return <FileText className={iconClass} />;
    case 'lead':
      return <Search className={iconClass} />;
    case 'duplicate':
      return <Users className={iconClass} />;
    case 'validation':
      return <AlertCircle className={iconClass} />;
    case 'discovery':
      return <Link2 className={iconClass} />;
    default:
      return <Lightbulb className={iconClass} />;
  }
};

// Status badge for saved investigations
const StatusBadge = ({ status }) => {
  const styles = {
    open: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
    in_progress: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
    resolved: 'bg-primary/10 text-primary border-primary/30'
  };
  
  const labels = {
    open: 'Open',
    in_progress: 'In Progress',
    resolved: 'Resolved'
  };
  
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-[0.1em] font-medium ${styles[status] || styles.open}`}>
      {labels[status] || 'Open'}
    </span>
  );
};

// Investigation Panel - Guided investigation flow
const InvestigationPanel = ({ insight, onClose, treeForNav, treeName, savedInvestigations, onSave, onUnsave }) => {
  const navigate = useNavigate();
  const treeBase = treeHref(treeForNav || {});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  
  // Check if this investigation is already saved
  const savedInv = savedInvestigations.find(inv => inv.insight_id === insight.id);
  const isSaved = !!savedInv;
  
  // Extract search parameters from insight
  const extractSurname = () => {
    if (insight.related_individuals && insight.related_individuals.length > 0) {
      const name = insight.related_individuals[0];
      const parts = name.trim().split(' ');
      return parts.length > 1 ? parts[parts.length - 1] : parts[0];
    }
    return '';
  };
  
  const extractDateRange = () => {
    if (insight.time_period) {
      const match = insight.time_period.match(/(\d{4})/g);
      if (match && match.length >= 2) {
        return { start: match[0], end: match[1] };
      } else if (match && match.length === 1) {
        return { start: match[0], end: String(parseInt(match[0]) + 40) };
      }
    }
    return { start: '', end: '' };
  };
  
  const surname = extractSurname();
  const dateRange = extractDateRange();
  const location = insight.location || '';
  
  // Navigate to Gene Engine with pre-filled search
  const handleSearchGeneEngine = () => {
    const params = new URLSearchParams();
    if (surname) params.set('name', surname);
    if (dateRange.start) params.set('yearStart', dateRange.start);
    if (dateRange.end) params.set('yearEnd', dateRange.end);
    if (location) params.set('location', location);
    
    navigate(`/gene-engine?${params.toString()}`);
  };
  
  // Navigate to person profile (deep link)
  const handleViewProfile = (personName) => {
    navigate(`${treeBase}?search=${encodeURIComponent(personName)}`);
  };
  
  // Navigate to filtered people view
  const handleViewFiltered = () => {
    if (location) {
      navigate(`${treeBase}?filter=location&value=${encodeURIComponent(location)}`);
    } else if (surname) {
      navigate(`${treeBase}?search=${encodeURIComponent(surname)}`);
    } else {
      navigate(treeBase);
    }
  };
  
  // Save investigation
  const handleSaveInvestigation = async () => {
    setSaving(true);
    try {
      await onSave({
        tree_id: treeForNav?.id,
        tree_name: treeName,
        insight_id: insight.id,
        insight_type: insight.insight_type,
        insight_title: insight.title,
        insight_description: insight.description,
        search_params: { surname, yearStart: dateRange.start, yearEnd: dateRange.end, location },
        notes: notes || null
      });
      toast.success('Investigation saved to research journal');
      setShowNotes(false);
    } catch (error) {
      toast.error('Could not save investigation');
    } finally {
      setSaving(false);
    }
  };
  
  // Remove saved investigation
  const handleUnsave = async () => {
    if (savedInv) {
      await onUnsave(savedInv.id);
      toast.success('Removed from research journal');
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="overflow-hidden"
    >
      <div className="mt-3 pt-3 border-t border-border" data-testid="investigation-panel">
        {/* Panel Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Search className="w-3 h-3 text-[#C9A961]" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium">
              Investigation Context
            </span>
          </div>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-secondary transition-colors"
            data-testid="close-investigation-btn"
          >
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        </div>
        
        {/* Search Parameters Preview */}
        <div className="space-y-2 mb-4">
          {surname && (
            <div className="flex items-center gap-2 text-[11px]">
              <User className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">Surname:</span>
              <span className="text-foreground font-medium">{surname}</span>
            </div>
          )}
          {(dateRange.start || dateRange.end) && (
            <div className="flex items-center gap-2 text-[11px]">
              <Calendar className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">Period:</span>
              <span className="text-foreground font-medium">
                {dateRange.start}{dateRange.end && ` - ${dateRange.end}`}
              </span>
            </div>
          )}
          {location && (
            <div className="flex items-center gap-2 text-[11px]">
              <MapPin className="w-3 h-3 text-muted-foreground" />
              <span className="text-muted-foreground">Location:</span>
              <span className="text-foreground font-medium">{location}</span>
            </div>
          )}
        </div>
        
        {/* Timeline Context for gaps */}
        {insight.insight_type === 'gap' && (
          <div className="bg-secondary/50 rounded-lg p-3 mb-4">
            <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-2">
              Missing Generational Gap
            </p>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {insight.suggestion || `Records from ${dateRange.start}-${dateRange.end} may contain ancestors.`}
            </p>
          </div>
        )}
        
        {/* Suggested Research */}
        <div className="bg-secondary/30 rounded-lg p-3 mb-4">
          <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-2">
            Suggested Research
          </p>
          <ul className="space-y-1.5">
            {insight.insight_type === 'gap' && (
              <li className="text-[11px] text-muted-foreground flex items-start gap-2">
                <span className="text-[#C9A961] mt-0.5">•</span>
                <span>Search for {surname} family records in the estimated period</span>
              </li>
            )}
            {insight.insight_type === 'migration' && (
              <li className="text-[11px] text-muted-foreground flex items-start gap-2">
                <span className="text-[#C9A961] mt-0.5">•</span>
                <span>Check immigration or relocation records for {location}</span>
              </li>
            )}
            {insight.insight_type === 'connection' && (
              <li className="text-[11px] text-muted-foreground flex items-start gap-2">
                <span className="text-[#C9A961] mt-0.5">•</span>
                <span>Look for extended family connections in {location || 'this region'}</span>
              </li>
            )}
            <li className="text-[11px] text-muted-foreground flex items-start gap-2">
              <span className="text-[#C9A961] mt-0.5">•</span>
              <span>Cross-reference with census or vital records</span>
            </li>
          </ul>
        </div>
        
        {/* Notes input for saving */}
        <AnimatePresence>
          {showNotes && !isSaved && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4"
            >
              <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-2">
                Research Notes (Optional)
              </p>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this investigation..."
                className="min-h-[60px] text-[11px] bg-background border-input text-foreground placeholder:text-muted-foreground"
                data-testid="investigation-notes"
              />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Saved investigation notes display */}
        {isSaved && savedInv.notes && (
          <div className="bg-secondary/30 rounded-lg p-3 mb-4">
            <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-2 flex items-center gap-1">
              <FileText className="w-3 h-3" />
              Your Research Notes
            </p>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {savedInv.notes}
            </p>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="space-y-2">
          {/* Search Gene Engine - Primary action */}
          <Button
            onClick={handleSearchGeneEngine}
            className="w-full h-8 text-[11px] bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
            data-testid="search-gene-engine-btn"
          >
            <Dna className="w-3 h-3 mr-2" />
            Search Gene Engine
            <ArrowRight className="w-3 h-3 ml-auto" />
          </Button>
          
          {/* Save Investigation Button */}
          {!isSaved ? (
            showNotes ? (
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowNotes(false)}
                  variant="outline"
                  className="flex-1 h-8 text-[11px] rounded-full border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveInvestigation}
                  disabled={saving}
                  className="flex-1 h-8 text-[11px] bg-[#C9A961]/20 hover:bg-[#C9A961]/30 text-[#C9A961] rounded-full border border-[#C9A961]/30"
                  data-testid="confirm-save-btn"
                >
                  {saving ? (
                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                  ) : (
                    <Bookmark className="w-3 h-3 mr-2" />
                  )}
                  Save to Journal
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setShowNotes(true)}
                variant="outline"
                className="w-full h-8 text-[11px] rounded-full border-border text-muted-foreground hover:text-foreground hover:border-[#C9A961]/50 hover:bg-secondary"
                data-testid="save-investigation-btn"
              >
                <Bookmark className="w-3 h-3 mr-2" />
                Save Investigation
              </Button>
            )
          ) : (
            <Button
              onClick={handleUnsave}
              variant="outline"
              className="w-full h-8 text-[11px] rounded-full border-[#C9A961]/30 text-[#C9A961] hover:bg-[#C9A961]/10"
              data-testid="saved-investigation-btn"
            >
              <BookmarkCheck className="w-3 h-3 mr-2" />
              Saved to Research Journal
            </Button>
          )}
          
          {/* Deep links to related individuals */}
          {insight.related_individuals && insight.related_individuals.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {insight.related_individuals.slice(0, 2).map((person, idx) => (
                <button
                  key={idx}
                  onClick={() => handleViewProfile(person)}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] text-muted-foreground hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-full transition-colors"
                  data-testid={`view-profile-${idx}`}
                >
                  <User className="w-2.5 h-2.5" />
                  {person.split(' ')[0]}
                </button>
              ))}
              {insight.location && (
                <button
                  onClick={handleViewFiltered}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] text-muted-foreground hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-full transition-colors"
                  data-testid="view-filtered-btn"
                >
                  <Filter className="w-2.5 h-2.5" />
                  View in {insight.location.split(',')[0]}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Single Insight Card with Investigation
const InsightCard = ({ insight, index, treeForNav, treeName, savedInvestigations, onSave, onUnsave }) => {
  const [isInvestigating, setIsInvestigating] = useState(false);
  const navigate = useNavigate();
  const treeBase = treeHref(treeForNav || {});
  
  // Check if saved
  const isSaved = savedInvestigations.some(inv => inv.insight_id === insight.id);
  
  // Handle click on related individual name - deep link
  const handlePersonClick = (personName) => {
    navigate(`${treeBase}?search=${encodeURIComponent(personName)}`);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4, ease: "easeOut" }}
      className="group"
      data-testid={`insight-card-${insight.id}`}
    >
      <div className="py-4 border-b border-border/50 last:border-0">
        {/* Header with icon, title, and confidence */}
        <div className="flex items-start gap-3 mb-2">
          <div className="flex-shrink-0 mt-0.5">
            <InsightIcon type={insight.insight_type} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-[13px] font-medium text-foreground tracking-wide">
                {insight.title}
              </h4>
              <ConfidenceBadge confidence={insight.confidence} />
              {isSaved && (
                <BookmarkCheck className="w-3 h-3 text-[#C9A961]" />
              )}
            </div>
          </div>
        </div>
        
        {/* Description */}
        <p className="text-[12px] text-muted-foreground leading-relaxed pl-6 mb-2">
          {insight.description}
        </p>
        
        {/* Related individuals - clickable deep links */}
        {insight.related_individuals && insight.related_individuals.length > 0 && (
          <div className="pl-6 mb-2">
            <div className="flex flex-wrap gap-1">
              {insight.related_individuals.slice(0, 3).map((person, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePersonClick(person)}
                  className="text-[11px] text-muted-foreground hover:text-[#C9A961] font-light transition-colors cursor-pointer"
                  data-testid={`person-link-${idx}`}
                >
                  {person}{idx < Math.min(insight.related_individuals.length, 3) - 1 && ' · '}
                </button>
              ))}
              {insight.related_individuals.length > 3 && (
                <span className="text-[11px] text-muted-foreground/70 font-light">
                  +{insight.related_individuals.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Time period and location metadata */}
        <div className="flex items-center gap-3 pl-6 flex-wrap">
          {insight.time_period && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />
              {insight.time_period}
            </span>
          )}
          {insight.location && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <MapPin className="w-2.5 h-2.5" />
              {insight.location}
            </span>
          )}
        </div>
        
        {/* Suggestion - subtle, investigative */}
        {insight.suggestion && (
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-[11px] text-[#C9A961]/80 pl-6 mt-2 italic font-light"
          >
            {insight.suggestion}
          </motion.p>
        )}
        
        {/* Investigate Button - appears on hover or always visible on mobile */}
        <div className="pl-6 mt-3">
          <button
            onClick={() => setIsInvestigating(!isInvestigating)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-full transition-all duration-200 ${
              isInvestigating
                ? 'bg-[#C9A961]/20 text-[#C9A961] border border-[#C9A961]/30'
                : 'bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary border border-transparent'
            }`}
            data-testid={`investigate-btn-${insight.id}`}
          >
            <Search className="w-3 h-3" />
            {isInvestigating ? 'Close Investigation' : 'Investigate This Branch'}
          </button>
        </div>
        
        {/* Investigation Panel */}
        <AnimatePresence>
          {isInvestigating && (
            <InvestigationPanel 
              insight={insight} 
              onClose={() => setIsInvestigating(false)}
              treeId={treeId}
              treeName={treeName}
              savedInvestigations={savedInvestigations}
              onSave={onSave}
              onUnsave={onUnsave}
            />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Source type labels for Research Journal
const SOURCE_TYPE_LABELS = {
  dna_insight: 'DNA Insight',
  dna_match: 'DNA Match',
  archive_record: 'Archive Record',
  lead: 'Investigation Lead',
  duplicate: 'Duplicate Candidate',
  validation: 'Validation Concern',
  discovery: 'Cross-Tree Match',
  gap: 'Generational Gap',
  migration: 'Migration',
  pattern: 'Pattern',
  connection: 'Connection'
};

// Saved Investigations List (Research Journal)
const SavedInvestigationsList = ({ investigations, onResume, onUpdateStatus, onDelete }) => {
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [groupBy, setGroupBy] = useState('none'); // 'none' | 'type' | 'status'

  if (investigations.length === 0) return null;

  const filtered = investigations.filter((inv) => {
    if (filterType !== 'all' && inv.insight_type !== filterType) return false;
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    return true;
  });

  const typeCounts = {};
  const statusCounts = {};
  investigations.forEach((inv) => {
    typeCounts[inv.insight_type] = (typeCounts[inv.insight_type] || 0) + 1;
    statusCounts[inv.status] = (statusCounts[inv.status] || 0) + 1;
  });

  const displayList = groupBy === 'type'
    ? Object.entries(
        filtered.reduce((acc, inv) => {
          const k = inv.insight_type || 'other';
          if (!acc[k]) acc[k] = [];
          acc[k].push(inv);
          return acc;
        }, {})
      ).sort((a, b) => b[1].length - a[1].length)
    : groupBy === 'status'
    ? Object.entries(
        filtered.reduce((acc, inv) => {
          const k = inv.status || 'open';
          if (!acc[k]) acc[k] = [];
          acc[k].push(inv);
          return acc;
        }, {})
      ).sort((a, b) => ['open', 'in_progress', 'resolved'].indexOf(a[0]) - ['open', 'in_progress', 'resolved'].indexOf(b[0]))
    : [['all', filtered]];

  const JournalEntry = ({ inv }) => (
    <motion.div
      key={inv.id}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-secondary/40 rounded-lg p-3 group"
      data-testid={`saved-inv-${inv.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <InsightIcon type={inv.insight_type} />
          <span className="text-[11px] text-foreground font-medium truncate">
            {inv.insight_title}
          </span>
        </div>
        <StatusBadge status={inv.status} />
      </div>

      <p className="text-[10px] text-muted-foreground mb-2 line-clamp-2">
        {inv.insight_description}
      </p>

      {inv.linked_individual_ids?.length > 0 && (
        <p className="text-[9px] text-muted-foreground/70 mb-1">
          Linked: {inv.linked_individual_ids.length} person{inv.linked_individual_ids.length > 1 ? 's' : ''}
        </p>
      )}

      {inv.notes && (
        <p className="text-[10px] text-muted-foreground/70 italic mb-2 line-clamp-1">
          Note: {inv.notes}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={() => onResume(inv)}
          className="flex items-center gap-1 px-2 py-1 text-[9px] text-muted-foreground hover:text-[#C9A961] bg-secondary/50 hover:bg-secondary rounded-full transition-colors"
          data-testid={`resume-inv-${inv.id}`}
        >
          <Search className="w-2.5 h-2.5" />
          Resume
        </button>

        {inv.status !== 'resolved' && (
          <button
            onClick={() => onUpdateStatus(inv.id, 'resolved')}
            className="flex items-center gap-1 px-2 py-1 text-[9px] text-muted-foreground hover:text-primary bg-secondary/50 hover:bg-secondary rounded-full transition-colors"
            data-testid={`resolve-inv-${inv.id}`}
          >
            <CheckCircle2 className="w-2.5 h-2.5" />
            Resolve
          </button>
        )}

        <button
          onClick={() => onDelete(inv.id)}
          className="flex items-center gap-1 px-2 py-1 text-[9px] text-muted-foreground hover:text-destructive bg-secondary/50 hover:bg-secondary rounded-full transition-colors opacity-0 group-hover:opacity-100"
          data-testid={`delete-inv-${inv.id}`}
        >
          <X className="w-2.5 h-2.5" />
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-3">
        <BookmarkCheck className="w-3.5 h-3.5 text-[#C9A961]" />
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.2em]">
          Research Journal
        </p>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#C9A961]/10 text-[#C9A961] border border-[#C9A961]/20">
          {investigations.length}
        </span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="h-6 text-[9px] bg-secondary border border-border rounded px-2 text-muted-foreground"
        >
          <option value="all">All types</option>
          {Object.entries(typeCounts).map(([t, c]) => (
            <option key={t} value={t}>
              {SOURCE_TYPE_LABELS[t] || t}: {c}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="h-6 text-[9px] bg-secondary border border-border rounded px-2 text-muted-foreground"
        >
          <option value="all">All statuses</option>
          <option value="open">Open ({statusCounts.open || 0})</option>
          <option value="in_progress">In progress ({statusCounts.in_progress || 0})</option>
          <option value="resolved">Resolved ({statusCounts.resolved || 0})</option>
        </select>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          className="h-6 text-[9px] bg-secondary border border-border rounded px-2 text-muted-foreground"
        >
          <option value="none">No grouping</option>
          <option value="type">Group by type</option>
          <option value="status">Group by status</option>
        </select>
      </div>

      <div className="space-y-2">
        {displayList.map(([groupKey, items]) => (
          <div key={groupKey}>
            {groupBy !== 'none' && (
              <p className="text-[9px] text-muted-foreground/70 uppercase tracking-wider mb-1.5 mt-3 first:mt-0">
                {groupBy === 'type' ? (SOURCE_TYPE_LABELS[groupKey] || groupKey) : groupKey.replace('_', ' ')}
              </p>
            )}
            {items.slice(0, groupBy === 'none' ? 5 : 99).map((inv) => (
              <JournalEntry key={inv.id} inv={inv} />
            ))}
            {items.length > (groupBy === 'none' ? 5 : 99) && (
              <p className="text-[10px] text-muted-foreground text-center py-1">
                +{items.length - (groupBy === 'none' ? 5 : 99)} more
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


// Main Family Insights Component
const FamilyInsights = ({ treeId, treeSlug }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [savedInvestigations, setSavedInvestigations] = useState([]);
  const navigate = useNavigate();

  // Fetch insights
  useEffect(() => {
    const fetchData = async () => {
      if (!treeId) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/trees/${treeId}/insights`);
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch family insights:', err);
        setError('Unable to analyze family patterns');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [treeId]);

  // Fetch saved investigations
  useEffect(() => {
    const fetchSavedInvestigations = async () => {
      try {
        const response = await axios.get(`${API}/investigations`, {
          params: { tree_id: treeId }
        });
        setSavedInvestigations(response.data);
      } catch (err) {
        console.error('Failed to fetch saved investigations:', err);
      }
    };

    if (treeId) {
      fetchSavedInvestigations();
    }
  }, [treeId]);

  // Save investigation handler
  const handleSaveInvestigation = async (investigationData) => {
    const response = await axios.post(`${API}/investigations`, investigationData);
    setSavedInvestigations(prev => [response.data, ...prev]);
  };

  // Unsave (delete) investigation handler
  const handleUnsaveInvestigation = async (investigationId) => {
    await axios.delete(`${API}/investigations/${investigationId}`);
    setSavedInvestigations(prev => prev.filter(inv => inv.id !== investigationId));
  };

  // Update investigation status
  const handleUpdateStatus = async (investigationId, status) => {
    await axios.patch(`${API}/investigations/${investigationId}`, { status });
    setSavedInvestigations(prev => 
      prev.map(inv => inv.id === investigationId ? { ...inv, status } : inv)
    );
    toast.success(status === 'resolved' ? 'Investigation marked as resolved' : 'Status updated');
  };

  // Resume investigation - navigate to appropriate context
  const handleResumeInvestigation = (investigation) => {
    if (investigation.insight_type === 'dna_insight' || investigation.insight_type === 'dna_match') {
      const profileId = investigation.search_params?.profile_id;
      navigate(profileId ? `/dna?profile=${profileId}` : '/dna');
      return;
    }
    if (investigation.insight_type === 'archive_record') {
      const params = new URLSearchParams();
      if (investigation.search_params?.primary_name) params.set('name', investigation.search_params.primary_name);
      if (investigation.search_params?.record_year) params.set('yearStart', investigation.search_params.record_year);
      if (investigation.search_params?.location) params.set('location', investigation.search_params.location);
      navigate(`/gene-engine?${params.toString()}`);
      return;
    }
    if (investigation.insight_type === 'duplicate' && investigation.linked_individual_ids?.[0]) {
      navigate(personHref({ id: investigation.linked_individual_ids[0] }));
      return;
    }
    if (investigation.insight_type === 'validation' && investigation.linked_individual_ids?.[0]) {
      navigate(`${personHref({ id: investigation.linked_individual_ids[0] })}?highlight=relationship`);
      return;
    }
    if (investigation.insight_type === 'discovery' && investigation.tree_id) {
      navigate(treeHref({ id: investigation.tree_id }));
      return;
    }
    const params = new URLSearchParams();
    if (investigation.search_params?.surname) params.set('name', investigation.search_params.surname);
    if (investigation.search_params?.yearStart) params.set('yearStart', investigation.search_params.yearStart);
    if (investigation.search_params?.yearEnd) params.set('yearEnd', investigation.search_params.yearEnd);
    if (investigation.search_params?.location) params.set('location', investigation.search_params.location);
    navigate(`/gene-engine?${params.toString()}`);
  };

  if (loading) {
    return (
      <Card className="bg-card border-border" data-testid="family-insights-loading">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center text-muted-foreground">
            <div className="w-6 h-6 border border-border border-t-[#C9A961] rounded-full animate-spin mb-4" />
            <span className="text-[11px] uppercase tracking-[0.15em]">Analyzing patterns</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-10 text-center text-muted-foreground">
          <p className="text-[12px]">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border overflow-hidden" data-testid="family-insights">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-0 pt-5 px-5 cursor-pointer hover:bg-secondary/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-3.5 h-3.5 text-[#C9A961]" />
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.2em]">
                  Family Insights
                </p>
                {data?.total_insights > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#C9A961]/10 text-[#C9A961] border border-[#C9A961]/20">
                    {data.total_insights}
                  </span>
                )}
                {savedInvestigations.length > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border flex items-center gap-1">
                    <BookmarkCheck className="w-2.5 h-2.5" />
                    {savedInvestigations.length}
                  </span>
                )}
              </div>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-4 px-5 pb-5">
            {/* Saved Investigations / Research Journal */}
            <SavedInvestigationsList 
              investigations={savedInvestigations}
              onResume={handleResumeInvestigation}
              onUpdateStatus={handleUpdateStatus}
              onDelete={handleUnsaveInvestigation}
            />
            
            <AnimatePresence mode="wait">
              {!data || data.insights.length === 0 ? (
                <EmptyState
                  icon={Lightbulb}
                  title="No insights yet"
                  description="Add more family members to discover patterns."
                  variant="compact"
                />
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Subtitle */}
                  <p className="text-[11px] text-muted-foreground mb-4 font-light">
                    Patterns and connections discovered in your archive
                  </p>
                  
                  {/* Insights list */}
                  <div className="space-y-0">
                    {data.insights.map((insight, index) => (
                      <InsightCard 
                        key={insight.id} 
                        insight={insight} 
                        index={index}
                        treeForNav={{ id: treeId, slug: treeSlug }}
                        treeName={data.tree_name}
                        savedInvestigations={savedInvestigations}
                        onSave={handleSaveInvestigation}
                        onUnsave={handleUnsaveInvestigation}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default FamilyInsights;
